/**
 * SIP Authentication Page
 * 
 * Manage SIP Digest Authentication credentials.
 * Uses the sip_authentication table via the Sip domain in @sbclite/core.
 * 
 * Authentication realm is derived from the linked Host's domain, providing:
 * - Single source of truth for authentication domain
 * - Automatic consistency between SIP identity and auth realm
 * - Simplified configuration (select host instead of typing realm)
 */

import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Shield,
  Plus,
  RefreshCw,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Key,
  User,
  Activity,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SipAuthCredential } from '../../services/api';
import { useHosts } from '../../hooks/useHosts';
import { useViewMode } from '../../hooks/useViewMode';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  DeleteConfirmModal,
  StatusBadge,
  enabledToStatus,
  Modal,
} from '../../components/common';

// ========================================
// HELPER FUNCTIONS
// ========================================

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

function isLocked(credential: SipAuthCredential): boolean {
  if (!credential.lockedUntil) return false;
  return new Date(credential.lockedUntil) > new Date();
}

function getLockTimeRemaining(lockedUntil: string): string {
  const remaining = new Date(lockedUntil).getTime() - Date.now();
  if (remaining <= 0) return 'Expired';
  const minutes = Math.ceil(remaining / 60000);
  return `${minutes} min`;
}

// ========================================
// TYPES
// ========================================

type FilterType = 'all' | 'active' | 'disabled' | 'locked';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Credentials' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'locked', label: 'Locked' },
];

// ========================================
// MAIN COMPONENT
// ========================================

export default function SipAuthenticationPage() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<SipAuthCredential | null>(null);
  const [editingCredential, setEditingCredential] = useState<SipAuthCredential | null>(null);

  // Queries
  const credentialsQuery = useQuery({
    queryKey: ['sip-auth-credentials'],
    queryFn: async () => {
      const response = await api.getSipAuthCredentials();
      return response.data;
    },
  });

  const statsQuery = useQuery({
    queryKey: ['sip-auth-stats'],
    queryFn: async () => {
      const response = await api.getSipAuthStats();
      return response.data;
    },
  });

  const credentials = useMemo(() => credentialsQuery.data?.credentials || [], [credentialsQuery.data]);
  const stats = statsQuery.data;

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (credential: SipAuthCredential) => {
      return api.deleteSipAuthCredential(credential.username, credential.hostId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-auth-credentials'] });
      queryClient.invalidateQueries({ queryKey: ['sip-auth-stats'] });
      setShowDeleteModal(false);
      setSelectedCredential(null);
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async (credential: SipAuthCredential) => {
      return api.unlockSipAuthAccount(credential.username, credential.hostId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-auth-credentials'] });
      queryClient.invalidateQueries({ queryKey: ['sip-auth-stats'] });
    },
  });

  const cleanupMutation = useMutation({
    mutationFn: () => api.cleanupSipAuth(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-auth-credentials'] });
      queryClient.invalidateQueries({ queryKey: ['sip-auth-stats'] });
    },
  });

  // Filter credentials
  const filteredCredentials = useMemo(() => {
    const search = searchQuery.toLowerCase();

    return credentials.filter((cred: SipAuthCredential) => {
      // Search filter
      const matchesSearch = !search ||
        cred.username.toLowerCase().includes(search) ||
        cred.realm.toLowerCase().includes(search) ||
        (cred.hostName || '').toLowerCase().includes(search);

      if (!matchesSearch) return false;

      // Type filter
      switch (filterType) {
        case 'active':
          return cred.enabled && !isLocked(cred);
        case 'disabled':
          return !cred.enabled;
        case 'locked':
          return isLocked(cred);
        default:
          return true;
      }
    });
  }, [credentials, searchQuery, filterType]);

  const handleDeleteCredential = (credential: SipAuthCredential) => {
    setSelectedCredential(credential);
    setShowDeleteModal(true);
  };

  const handleEditCredential = (credential: SipAuthCredential) => {
    setEditingCredential(credential);
    setShowCreateModal(true);
  };

  // Loading state
  if (credentialsQuery.isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (credentialsQuery.error) {
    return (
      <ErrorState
        title="Failed to load credentials"
        message={credentialsQuery.error instanceof Error ? credentialsQuery.error.message : 'Unknown error'}
        onRetry={() => credentialsQuery.refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="SIP Authentication"
        description="Manage SIP Digest Authentication credentials (RFC 3261/2617)"
        onRefresh={() => {
          credentialsQuery.refetch();
          statsQuery.refetch();
        }}
        isRefreshing={credentialsQuery.isFetching}
        primaryAction={{
          label: 'Add Credentials',
          icon: Plus,
          onClick: () => {
            setEditingCredential(null);
            setShowCreateModal(true);
          },
        }}
        actions={
          <button
            onClick={() => cleanupMutation.mutate()}
            disabled={cleanupMutation.isPending}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Clean up expired nonces and unlock expired lockouts"
          >
            <Activity className={`w-4 h-4 ${cleanupMutation.isPending ? 'animate-spin' : ''}`} />
            Cleanup
          </button>
        }
      />

      {/* Statistics */}
      {stats && (
        <StatsGrid columns={5}>
          <StatCard
            label="Total Credentials"
            value={stats.totalCredentials}
            icon={Key}
            iconBgColor="bg-blue-100"
            iconColor="text-blue-600"
          />
          <StatCard
            label="Active"
            value={stats.activeCredentials}
            icon={CheckCircle}
            iconBgColor="bg-green-100"
            iconColor="text-green-600"
            valueColor="text-green-600"
          />
          <StatCard
            label="Locked"
            value={stats.lockedCredentials}
            icon={Lock}
            iconBgColor="bg-red-100"
            iconColor="text-red-600"
            valueColor="text-red-600"
          />
          <StatCard
            label="Successful Auths"
            value={stats.totalSuccessfulAuths}
            icon={Shield}
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
          />
          <StatCard
            label="Failed Attempts"
            value={stats.totalFailedAttempts}
            icon={AlertTriangle}
            iconBgColor="bg-orange-100"
            iconColor="text-orange-600"
            valueColor="text-orange-600"
          />
        </StatsGrid>
      )}

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search by username, realm, or host..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Credentials Display */}
      {filteredCredentials.length === 0 ? (
        <EmptyState
          icon={Key}
          title="No credentials found"
          description="Get started by adding SIP authentication credentials."
          isFiltered={!!(searchQuery || filterType !== 'all')}
          action={{
            label: 'Add Credentials',
            icon: Plus,
            onClick: () => setShowCreateModal(true),
          }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCredentials.map((credential: SipAuthCredential) => (
            <ItemCard
              key={credential.id}
              icon={
                <ItemCardIcon
                  icon={<User className={`w-5 h-5 ${credential.enabled && !isLocked(credential) ? 'text-blue-600' : 'text-gray-400'}`} />}
                  bgColor={credential.enabled && !isLocked(credential) ? 'bg-blue-100' : 'bg-gray-100'}
                />
              }
              title={credential.username}
              subtitle={credential.hostName || credential.realm}
              status={
                isLocked(credential) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    <Lock className="w-3 h-3" />
                    Locked
                  </span>
                ) : (
                  <StatusBadge status={enabledToStatus(credential.enabled)} />
                )
              }
              details={[
                { label: 'Realm', value: credential.realm },
                { label: 'Success', value: <span className="text-green-600">{credential.successfulAuths}</span> },
                { label: 'Failed', value: <span className="text-red-600">{credential.failedAttempts}</span> },
              ]}
              actions={
                <ItemActions
                  variant="card"
                  actions={[
                    ...(isLocked(credential)
                      ? [{ kind: 'unlock' as const, onClick: () => unlockMutation.mutate(credential), disabled: unlockMutation.isPending }]
                      : []),
                    { kind: 'edit', onClick: () => handleEditCredential(credential) },
                    { kind: 'delete', onClick: () => handleDeleteCredential(credential) },
                  ]}
                />
              }
            />
          ))}
        </div>
      ) : (
        // List View (Table)
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host / Realm</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth Stats</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCredentials.map((credential: SipAuthCredential) => (
                <tr key={credential.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{credential.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{credential.hostName || 'Unknown Host'}</div>
                    <div className="text-xs text-gray-500">{credential.realm}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isLocked(credential) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        <Lock className="w-3 h-3" />
                        Locked ({getLockTimeRemaining(credential.lockedUntil!)})
                      </span>
                    ) : (
                      <StatusBadge status={enabledToStatus(credential.enabled)} />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col text-sm">
                      <span className="text-green-600">{credential.successfulAuths} success</span>
                      <span className="text-red-600">{credential.failedAttempts} failed</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(credential.lastSuccessfulAuth)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <ItemActions
                      variant="table"
                      actions={[
                        ...(isLocked(credential)
                          ? [{ kind: 'unlock' as const, title: 'Unlock account', onClick: () => unlockMutation.mutate(credential), disabled: unlockMutation.isPending }]
                          : []),
                        { kind: 'edit', onClick: () => handleEditCredential(credential) },
                        { kind: 'delete', onClick: () => handleDeleteCredential(credential) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About SIP Digest Authentication</p>
            <p>
              SIP Digest Authentication (RFC 3261/2617) secures SIP signaling by requiring devices to prove their identity 
              using a challenge-response mechanism. The authentication realm is derived from the host's domain, providing 
              a single source of truth for both SIP identity and authentication. Accounts are automatically locked after 
              5 failed attempts for 5 minutes.
            </p>
          </div>
        </div>
      </div>

      {/* Create/Edit Credential Modal */}
      {showCreateModal && (
        <CreateEditCredentialModal
          credential={editingCredential}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCredential(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sip-auth-credentials'] });
            queryClient.invalidateQueries({ queryKey: ['sip-auth-stats'] });
            setShowCreateModal(false);
            setEditingCredential(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal && !!selectedCredential}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCredential(null);
        }}
        onConfirm={() => selectedCredential && deleteMutation.mutate(selectedCredential)}
        title="Delete Credentials"
        entityType="credential"
        entityName={selectedCredential ? `${selectedCredential.username}@${selectedCredential.realm}` : ''}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

// ========================================
// CREATE/EDIT CREDENTIAL MODAL
// ========================================

interface CreateEditCredentialModalProps {
  credential: SipAuthCredential | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Form schema. Field-level rules mirror the core SipAuthCredential schemas
 * (username/password/host), extended with a UI-only confirmPassword and
 * edit-mode-optional password (blank = keep current). The API remains the
 * source of truth and revalidates with the core schema server-side.
 */
function makeCredentialFormSchema(isEditing: boolean) {
  return z
    .object({
      username: z.string().min(1, 'Username is required').max(100, 'Username must be 100 characters or less'),
      hostId: z.string().uuid('Please select a host'),
      password: z.string(),
      confirmPassword: z.string(),
      peerId: z.string(),
      enabled: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!isEditing && !data.password) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Password is required' });
      }
      if (data.password && data.password.length < 6) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Password must be at least 6 characters' });
      }
      if (data.password && data.password !== data.confirmPassword) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: 'Passwords do not match' });
      }
    });
}

type CredentialFormData = {
  username: string;
  hostId: string;
  password: string;
  confirmPassword: string;
  peerId: string;
  enabled: boolean;
};

const CreateEditCredentialModal: React.FC<CreateEditCredentialModalProps> = ({
  credential,
  onClose,
  onSuccess
}) => {
  const isEditing = !!credential;
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<CredentialFormData>({
    resolver: zodResolver(makeCredentialFormSchema(isEditing)),
    defaultValues: {
      username: credential?.username || '',
      hostId: credential?.hostId || '',
      password: '',
      confirmPassword: '',
      peerId: credential?.peerId || '',
      enabled: credential?.enabled ?? true,
    },
  });

  // Get hosts for dropdown
  const { data: hostsResponse, isLoading: hostsLoading } = useHosts();
  const hosts = useMemo(() => hostsResponse?.data || [], [hostsResponse]);

  const hostId = form.watch('hostId');
  const password = form.watch('password');

  // Set default host when hosts load (for create mode)
  React.useEffect(() => {
    if (!isEditing && hosts.length > 0 && !hostId) {
      const defaultHost = hosts.find((h) => h.isDefault);
      if (defaultHost) {
        form.setValue('hostId', defaultHost.id, { shouldValidate: true });
      }
    }
  }, [hosts, isEditing, hostId, form]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: CredentialFormData) => {
      return api.createSipAuthCredential({
        username: data.username,
        password: data.password,
        hostId: data.hostId,
        peerId: data.peerId || undefined,
        enabled: data.enabled,
      });
    },
    onSuccess: () => onSuccess(),
    onError: (err: Error) => {
      const apiErr = err as Error & { response?: { data?: { error?: string } } };
      setError(apiErr.response?.data?.error || 'Failed to create credentials');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CredentialFormData) => {
      if (!credential) throw new Error('No credential selected');
      return api.updateSipAuthCredential(
        credential.username,
        {
          password: data.password || undefined,
          hostId: data.hostId !== credential.hostId ? data.hostId : undefined,
          peerId: data.peerId || null,
          enabled: data.enabled,
        },
        credential.hostId
      );
    },
    onSuccess: () => onSuccess(),
    onError: (err: Error) => {
      const apiErr = err as Error & { response?: { data?: { error?: string } } };
      setError(apiErr.response?.data?.error || 'Failed to update credentials');
    },
  });

  const onSubmit = async (data: CredentialFormData) => {
    setError(null);
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Get selected host info for realm preview
  const selectedHost = hosts.find((h) => h.id === hostId);
  const realmPreview = selectedHost?.domain || selectedHost?.externalIp || selectedHost?.name || '';

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEditing ? 'Edit Credentials' : 'Create SIP Authentication'}
      subtitle={isEditing ? 'Update credential settings' : 'Add new SIP Digest Authentication credentials'}
      size="lg"
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Username *
          </label>
          <input
            type="text"
            {...form.register('username')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., user1"
            disabled={isEditing}
          />
          {form.formState.errors.username && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.username.message}</p>
          )}
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">Username cannot be changed after creation</p>
          )}
        </div>

        {/* Host Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SIP Identity (Authentication Domain) *
          </label>
          <select
            {...form.register('hostId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={hostsLoading}
          >
            <option value="">Select a host...</option>
            {hosts.map((host) => (
              <option key={host.id} value={host.id}>
                {host.name} ({host.domain || host.externalIp || 'No domain'})
                {host.isDefault ? ' (Default)' : ''}
              </option>
            ))}
          </select>
          {form.formState.errors.hostId && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.hostId.message}</p>
          )}
          {realmPreview && (
            <p className="text-xs text-gray-500 mt-1">
              Realm will be: <span className="font-mono">{realmPreview}</span>
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isEditing ? 'New Password' : 'Password'} {!isEditing && '*'}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...form.register('password')}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isEditing ? 'Leave blank to keep current' : 'Enter password'}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {form.formState.errors.password && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.password.message}</p>
          )}
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
          )}
        </div>

        {/* Confirm Password - only when password is entered */}
        {password && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              {...form.register('confirmPassword')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Confirm password"
            />
            {form.formState.errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.confirmPassword.message}</p>
            )}
          </div>
        )}

        {/* Peer ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Peer ID (optional)
          </label>
          <input
            type="text"
            {...form.register('peerId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Link to SIP peer (optional)"
          />
        </div>

        {/* Enabled */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="enabled"
            {...form.register('enabled')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="enabled" className="text-sm text-gray-700">
            Enable authentication
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEditing ? 'Save Changes' : 'Create Credentials'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
